/**
 * Pre-paint half of the 18+ gate (PLE-452). Dependency-free so the inline
 * boot script and the React host share one definition and a strip-types test
 * can run it without a browser.
 *
 * The root layout renders an opaque shield as the first thing in <body>.
 * CSS shows it unless <html data-age-gate="ok">. The boot script runs while
 * the HTML is parsed, before any page content paints, and marks the page
 * "ok" only when the visitor already confirmed (cookie) or the route is
 * exempt. Anything else, including a script error or disabled JavaScript,
 * keeps the shield up (fail closed).
 */

export const AGE_GATE_ATTRIBUTE = "data-age-gate"

export type AgeGateState = "ok" | "required"

export type AgeGateBootConfig = {
  cookieName: string
  exemptRoutes: readonly string[]
  locales: readonly string[]
}

/** Mirrors stripLocaleFromPath in src/shared/lib/i18n/config.ts. */
export function stripLocale(pathname: string, locales: readonly string[]): string {
  const segments = pathname.split("/").filter(Boolean)
  if (segments.length === 0) return "/"
  if (locales.indexOf(segments[0]) < 0) return pathname || "/"
  const stripped = "/" + segments.slice(1).join("/")
  return stripped === "/" ? "/" : stripped.replace(/\/$/, "")
}

export function isExemptPath(
  pathname: string,
  exemptRoutes: readonly string[],
  locales: readonly string[]
): boolean {
  const normalized = stripLocale(pathname, locales)
  return exemptRoutes.some(
    (route) => normalized === route || normalized.indexOf(route + "/") === 0
  )
}

export function hasCookie(cookieHeader: string, cookieName: string): boolean {
  return cookieHeader
    .split(";")
    .some((part) => part.trim().indexOf(cookieName + "=") === 0 && part.trim().length > cookieName.length + 1)
}

export function ageGateState(
  pathname: string,
  cookieHeader: string,
  config: AgeGateBootConfig
): AgeGateState {
  return isExemptPath(pathname, config.exemptRoutes, config.locales) ||
    hasCookie(cookieHeader, config.cookieName)
    ? "ok"
    : "required"
}

/** Writes the gate state on <html>; the shield CSS keys off it. */
export function setAgeGateState(state: AgeGateState): void {
  if (typeof document === "undefined") return
  document.documentElement.setAttribute(AGE_GATE_ATTRIBUTE, state)
}

/**
 * Inline ES5 script for the initial HTML: the same decision as ageGateState,
 * written out by hand because bundlers rename functions (so Function#toString
 * is not safe). scripts/test-age-gate-boot.mts runs this string and checks it
 * agrees with ageGateState on every route shape.
 */
export function buildAgeGateBootScript(config: AgeGateBootConfig): string {
  return (
    "(function(){try{" +
    "var c=" + JSON.stringify(config) + ";" +
    "var p=location.pathname||'/';" +
    "var s=p.split('/').filter(Boolean);" +
    "var n=p;" +
    "if(s.length===0){n='/'}" +
    "else if(c.locales.indexOf(s[0])>=0){n='/'+s.slice(1).join('/');n=n==='/'?'/':n.replace(/\\/$/,'')}" +
    "var ex=c.exemptRoutes.some(function(r){return n===r||n.indexOf(r+'/')===0});" +
    "var ck=document.cookie.split(';').some(function(x){x=x.trim();return x.indexOf(c.cookieName+'=')===0&&x.length>c.cookieName.length+1});" +
    "document.documentElement.setAttribute(" + JSON.stringify(AGE_GATE_ATTRIBUTE) + ",ex||ck?'ok':'required')" +
    "}catch(e){}})();"
  )
}
